/**
 * Vase Creation — shared add-to-cart for vc-* sections.
 * Uses Tinker's own cart: posts to /cart/add.js with Tinker's cart section ids,
 * dispatches Tinker's CartAddEvent (so the drawer, bubble and free-shipping bar
 * re-render), then opens Tinker's cart drawer. Never builds a second cart UI.
 *
 * Markup contract: any element with data-vc-add="<variant id>" inside [data-vc-section].
 * Optional: data-vc-qty="2".
 */
import { CartAddEvent } from '@theme/events';

const BUSY_LABEL = 'Adding…';

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

function openDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) {
    window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
    return;
  }
  // Tinker opens the drawer itself when "auto open" is on; otherwise we open it.
  if (!drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') drawer.open();
}

async function addToCart(variantId, quantity, button) {
  const url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  const sections = cartSectionIds();
  const body = { items: [{ id: Number(variantId), quantity: quantity }] };
  if (sections.length) body.sections = sections.join(',');

  const label = button ? button.innerHTML : '';
  if (button) {
    button.setAttribute('aria-busy', 'true');
    button.innerHTML = BUSY_LABEL;
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();

    if (!res.ok || json.status) {
      if (button) {
        button.innerHTML = json.description || json.message || 'Unavailable';
        setTimeout(() => (button.innerHTML = label), 2600);
      }
      return;
    }

    let cart;
    try {
      cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    } catch (e) {
      cart = undefined;
    }

    document.dispatchEvent(
      new CartAddEvent(cart, 'vc-' + variantId, {
        source: 'vc-section',
        itemCount: quantity,
        variantId: String(variantId),
        sections: json.sections,
      })
    );
    openDrawer();
    if (button) button.innerHTML = label;
  } catch (error) {
    console.error('[vc-cart]', error);
    if (button) button.innerHTML = label;
  } finally {
    if (button) button.removeAttribute('aria-busy');
  }
}

if (!window.__vcCartBound) {
  window.__vcCartBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-vc-add]') : null;
    if (!target || !target.closest('[data-vc-section]')) return;
    event.preventDefault();
    if (target.getAttribute('aria-busy') === 'true' || target.hasAttribute('disabled')) return;
    const id = target.getAttribute('data-vc-add');
    const qty = parseInt(target.getAttribute('data-vc-qty') || '1', 10) || 1;
    if (id) addToCart(id, qty, target);
  });
  window.VC = Object.assign(window.VC || {}, { addToCart });
}
